import React, { useEffect, useState } from 'react'

import { TextField } from '@mui/material'
import { observer } from 'mobx-react-lite'

import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import { Underline } from '@tiptap/extension-underline'
import UniqueID from '@tiptap-pro/extension-unique-id'

import { useStores } from '@trivie/core'
import { ResourceApi } from '@trivie/core/src/services/api-objects'
import { Text } from '@trivie/ui-web/components/text'
import { color } from '@trivie/ui-web/theme'
import { isVimeo, isYoutube } from '@trivie/core/src/utils/mimetype'

import { ResourceUploadDialog } from '../../screens/content/content-creation/components/resource-upload-dialog'
import { TTImage, Audio, Video } from '@trivie/tiptap/src/extensions'
import { DocumentToolbar } from './document-toolbar'
import { Markdown } from 'tiptap-markdown'

import './document-editor.css'

interface CardEditorProps {
  card: any
  quiz?: any
  existing?: any
  readonly?: boolean
}

export const CardEditor = observer((props: CardEditorProps) => {
  const { existing = [], quiz, card, readonly } = props
  const { utilityStore } = useStores()
  const { setSourceRefresh } = utilityStore
  const { audioMimetypes, imageMimetypes, videoMimetypes } = utilityStore

  const docEditor = useEditor(
    {
      editable: !readonly,
      onTransaction: ({ editor, transaction }) => {
        card?.setCardJSON({ nodes: editor.getJSON() })
        card?.setEmpty(editor.isEmpty)
      },
      onUpdate: ({ transaction }) => {},
      onBlur: ({ editor }) => {
        card?.setEmpty(editor.isEmpty)
        card?.setTouched(true)
        card?.setApproved(false)
      },
      extensions: [
        StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
        Markdown.configure({
          html: true, // Allow HTML input/output
          tightLists: true, // No <p> inside <li> in markdown output
          tightListClass: 'tight', // Add class to <ul> allowing you to remove <p> margins when tight
          bulletListMarker: '-', // <li> prefix in markdown output
          linkify: false, // Create links from "https://..." text
          breaks: true, // New lines (\n) in markdown input are converted to <br>
        }),
        Subscript,
        Superscript,
        Underline,
        UniqueID.configure({
          types: [
            'paragraph',
            'listItem',
            'bulletList',
            'orderedList',
            'heading',
            'image',
            'video',
            'audio',
          ],
          attributeName: 'node_id',
          filterTransaction: (transaction) => {
            // transaction.steps.map((step) => console.log(step.toJSON()))
            // true splits a new uid
            return true
          },
        }),
        Video.configure({
          // uploadVideo
        }),
        Audio,
        TTImage.configure({
          // @ts-ignore
          // uploadImage,
        }),
        Placeholder.configure({
          emptyEditorClass: 'is-editor-empty',
          placeholder:
            'Enter formatted text, images, lists, and more to be displayed as a card within a quiz.',
        }),
        // ImageResize,
        // Dropcursor,
      ],
      content: card?.card_markdown || card?.card_json?.nodes || {},
      editorProps: {
        attributes: {
          class: 'document-editor',
        },
      },
    },
    [],
  )

  const [open, setOpen] = useState<any>(false)

  if (!card) {
    return null
  }

  return (
    <>
      <div style={{ marginBottom: 16, position: 'relative' }}>
        <TextField
          fullWidth
          sx={{
            my: 2,
            '& .MuiInputBase-root': {
              fontSize: 17,
              fontFamily: 'Inter',
              fontWeight: 600,
              lineHeight: '22px',
            },
            '& .MuiInput-underline:after': {
              borderBottomColor:
                (card?.card_name?.length === 0 || card?.card_name?.length > 80) && card?.touched
                  ? color.red
                  : color.blue,
            },
            '& .MuiInput-underline:before': {
              borderBottomColor:
                (card?.card_name?.length === 0 || card?.card_name?.length > 80) && card?.touched
                  ? color.red
                  : color.shade5,
            },
          }}
          variant="standard"
          placeholder="Enter card title"
          value={card.card_name}
          helperText={!card?.card_name?.trim().length ? 'Card name cannot be blank' : ''}
          error={
            card?.touched &&
            (Boolean(card?.card_name?.length > 60) ||
              Boolean(card?.card_name?.length === 60) ||
              !card?.card_name?.trim().length)
          }
          onChange={(e) => {
            card.setCardName(e.target.value)
          }}
          onBlur={() => {
            card?.setApproved(false)
            card?.setTouched(true)
          }}
          multiline
        />
        {card?.card_name && card?.card_name?.length >= 20 && (
          <Text
            variant="body2"
            text={`${card?.card_name?.length ?? 0}/60`}
            style={{
              fontWeight: 700,
              position: 'absolute',
              bottom: 0,
              right: 0,
              color:
                card?.card_name?.length > 60
                  ? color.palette.red
                  : card?.card_name?.length > 40
                    ? color.yellow
                    : color.shade30,
            }}
          />
        )}
      </div>
      <div
        id="editor-container"
        style={{
          position: 'relative',
          flexDirection: 'column',
          border: '1px solid #EFF0F6',
          borderRadius: 8,
          paddingTop: readonly ? 8 : 40,
          overflow: 'hidden',
        }}
      >
        {!readonly && <DocumentToolbar editor={docEditor} handleAddMedia={() => setOpen(true)} />}
        <EditorContent editor={docEditor} />
      </div>
      <ResourceUploadDialog
        open={open}
        standalone
        showExisting
        quiz={quiz}
        card
        close={() => setOpen(false)}
        onUpload={(r) => {
          ResourceApi.getSourceDetails(r.source_id).then((res) => {
            setSourceRefresh(r.source_id, Date.now())
            let node
            if (imageMimetypes.includes(res.data.mime_type)) {
              node = docEditor?.schema.nodes.image.create({
                src: res.data.filename_or_url,
                source_id: r.source_id,
              })
            } else if (audioMimetypes.includes(res.data.mime_type)) {
              node = docEditor?.schema.nodes.audio.create({
                src: res.data.filename_or_url,
                source_id: r.source_id,
              })
            } else if (
              videoMimetypes.includes(res.data.mime_type) ||
              isYoutube(res.data.filename_or_url) ||
              isVimeo(res.data.filename_or_url)
            ) {
              node = docEditor?.schema.nodes.video.create({
                src: res.data.filename_or_url,
                source_id: r.source_id,
                controlsList: 'nodownload',
              })
            }
            const transaction = docEditor?.state.tr.insert(docEditor.state.selection.anchor, node!)
            docEditor?.view.dispatch(transaction!)
          })
        }}
      />
    </>
  )
})
